import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { Icon } from 'src/components/shared/Icon/Icon';
import { Header3 } from 'src/components/shared/Typography/Header3';
import { useReduxDispatch } from 'src/store';
import { MixtureDetailDto } from 'src/store/mixtures/mixture';
import {
  fetchMixtureAvgParameters,
  fetchMixtureSpecifications,
  fetchMixtureStdPractice
} from 'src/store/mixtures/mixtures.actions';
import {
  selectMixtureAvgParameters,
  selectMixtureSpecification,
  selectMixtureStdPractice
} from 'src/store/mixtures/mixtures.selectors';

interface Props {
  mixture: Nullable<MixtureDetailDto>;
}

export const MixtureParameterValues: React.FC<Props> = ({ mixture }) => {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const mixtureAvgParameters$ = useSelector(selectMixtureAvgParameters);
  const mixtureStdPractice$ = useSelector(selectMixtureStdPractice);
  const mixtureSpecification$ = useSelector(selectMixtureSpecification);

  useEffect(() => {
    if (mixture) {
      dispatch(fetchMixtureAvgParameters(mixture.id!));
      dispatch(fetchMixtureStdPractice(mixture.id!));
      dispatch(fetchMixtureSpecifications(mixture.id!));
    }
  }, [mixture, dispatch]);

  return (
    <Fragment>
      <Accordion disabled={!(mixtureAvgParameters$.length > 0)}>
        <AccordionSummary
          expandIcon={<Icon type="la-angle-down" />}
          id="panel1-header"
        >
          <Header3>{t('mixture.analysisValues')}</Header3>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid items={mixtureAvgParameters$} filterable={false}>
            <Column
              field="parameterName"
              width={200}
              title={t('parameter.singular')}
            />
            <Column
              field="methodName"
              width={200}
              title={t('method.singular')}
            />
            <Column
              field="avgParameterValue"
              width={200}
              title={t('mixture.avgParameterValue')}
              format="{0:n3}"
            />
            <Column
              width="120px"
              field="acceptedForProduction"
              title={t('production')}
              cell={props => <BooleanCell {...props} />}
            />
            <Column
              width="120px"
              field="acceptedForCustomer"
              title={t('customer.singular')}
              cell={props => <BooleanCell {...props} />}
            />
          </DataGrid>
        </AccordionDetails>
      </Accordion>
      <Accordion disabled={!(mixtureStdPractice$.length > 0)}>
        <AccordionSummary
          expandIcon={<Icon type="la-angle-down" />}
          id="panel2-header"
        >
          <Header3>{t('mixture.standardPractice')}</Header3>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid items={mixtureStdPractice$} filterable={false}>
            <Column
              field="parameterName"
              width={200}
              title={t('parameter.singular')}
            />
            <Column
              field="minValue"
              width={200}
              title={t('mixture.minValue')}
            />
            <Column
              field="maxValue"
              width={200}
              title={t('mixture.maxValue')}
            />
          </DataGrid>
        </AccordionDetails>
      </Accordion>
      <Accordion disabled={!(mixtureSpecification$.length > 0)}>
        <AccordionSummary
          expandIcon={<Icon type="la-angle-down" />}
          id="panel2-header"
        >
          <Header3>{t('specification.singular')}</Header3>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid items={mixtureSpecification$} filterable={false}>
            <Column
              field="parameterName"
              width={200}
              title={t('parameter.singular')}
            />
            <Column
              field="methodName"
              width={200}
              title={t('method.singular')}
            />
            <Column field="minProd" width={200} title={t('mixture.minProd')} />
            <Column field="maxProd" width={200} title={t('mixture.maxProd')} />
            <Column
              field="minCustomer"
              width={200}
              title={t('mixture.minCustomer')}
            />
            <Column
              field="maxCustomer"
              width={200}
              title={t('mixture.maxCustomer')}
            />
          </DataGrid>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};
